import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  ChartBar, Headset, Wrench, Megaphone, Package, CurrencyDollar,
  UsersThree, FileText, SignOut, List, WifiHigh, X, HardHat, Path, Globe, MagnifyingGlass, User, Phone,
  ClipboardText, ShieldCheck, MapTrifold, Scales, Star, Robot, CalendarBlank,
  CaretDown, CaretRight, HardDrives, WifiSlash, Cpu, ArrowsClockwise, GearSix
} from '@phosphor-icons/react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';

const SEARCH_TYPE_META = {
  odb: { label: 'ODB Boxes', badge: 'ODB', icon: '▣', tone: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  olt: { label: 'OLT Locations', badge: 'OLT', icon: '▣', tone: 'bg-red-50 text-red-800 border-red-200' },
  splitter: { label: 'Splitters', badge: 'Splitter', icon: '◆', tone: 'bg-amber-50 text-amber-800 border-amber-200' },
  backbone_route: { label: 'Backbone Fiber', badge: 'BB', icon: '━', tone: 'bg-rose-50 text-rose-800 border-rose-200' },
  distribution_route: { label: 'Distribution Fiber', badge: 'Distribution', icon: '━', tone: 'bg-blue-50 text-blue-800 border-blue-200' },
  drop_cable: { label: 'Drop Fiber', badge: 'Drop', icon: '╱', tone: 'bg-green-50 text-green-800 border-green-200' },
  customer: { label: 'Customers', badge: 'Customer', icon: '●', tone: 'bg-slate-50 text-slate-800 border-slate-200' },
};

const SEARCH_GROUP_ORDER = ['odb', 'olt', 'splitter', 'backbone_route', 'distribution_route', 'drop_cable', 'customer'];

const ROLE_NAV = {
  SuperAdmin: [
    { path: '/dashboard', label: 'Dashboard', icon: ChartBar },
    { path: '/customers', label: 'Customers', icon: User },
    { path: '/operator', label: 'Tickets', icon: Headset },
    { path: '/service-ops', label: 'Service Ops', icon: ClipboardText },
    { path: '/noc', label: 'NOC Center', icon: ShieldCheck, subItems: [
      { path: '/noc-olt?configure=1', label: 'MikroTik Configure', icon: GearSix },
      { path: '/noc-olt', label: 'OLT Management', icon: HardDrives },
      { path: '/noc-onu', label: 'ONU List', icon: Cpu },
      { path: '/noc-tr069', label: 'TR-069 / ACS', icon: GearSix },
      { path: '/noc-onu-auth', label: 'ONU Authorization', icon: ShieldCheck },
      { path: '/noc-zones', label: 'Zones', icon: MapTrifold },
      { path: '/noc-odbs', label: 'ODBs', icon: HardDrives },
      { path: '/noc-onu-types', label: 'ONU Types', icon: Cpu },
      { path: '/noc-speed-profiles', label: 'Speed Profiles', icon: WifiHigh },
      { path: '/noc-auth-presets', label: 'Auth Presets', icon: GearSix },
      { path: '/noc-general', label: 'General', icon: GearSix },
      { path: '/noc-sync-logs', label: 'Sync Logs', icon: ArrowsClockwise },
    ]},
    { path: '/imft', label: 'IMFT', icon: Wrench },
    { path: '/bmft', label: 'BMFT', icon: Path },
    { path: '/odb-search', label: 'ODB Search', icon: MagnifyingGlass },
    { path: '/fiber-map', label: 'Fiber Map', icon: MapTrifold },
    { path: '/marketing', label: 'Marketing', icon: Megaphone },
    { path: '/store', label: 'Store', icon: Package },
    { path: '/finance', label: 'Finance', icon: CurrencyDollar },
    { path: '/accounting', label: 'Accounting', icon: Scales },
    { path: '/hr', label: 'HR & Staff', icon: UsersThree },
    { path: '/reports', label: 'Reports', icon: FileText },
    { path: '/ai-manager', label: 'AI Manager', icon: Robot },
    { path: '/admin-schedule', label: 'Schedule View', icon: CalendarBlank },
  ],
  Admin: [
    { path: '/dashboard', label: 'Dashboard', icon: ChartBar },
    { path: '/customers', label: 'Customers', icon: User },
    { path: '/operator', label: 'Tickets', icon: Headset },
    { path: '/service-ops', label: 'Service Ops', icon: ClipboardText },
    { path: '/noc', label: 'NOC Center', icon: ShieldCheck, subItems: [
      { path: '/noc-olt?configure=1', label: 'MikroTik Configure', icon: GearSix },
      { path: '/noc-olt', label: 'OLT Management', icon: HardDrives },
      { path: '/noc-onu', label: 'ONU List', icon: Cpu },
      { path: '/noc-tr069', label: 'TR-069 / ACS', icon: GearSix },
      { path: '/noc-onu-auth', label: 'ONU Authorization', icon: ShieldCheck },
      { path: '/noc-zones', label: 'Zones', icon: MapTrifold },
      { path: '/noc-odbs', label: 'ODBs', icon: HardDrives },
      { path: '/noc-onu-types', label: 'ONU Types', icon: Cpu },
      { path: '/noc-speed-profiles', label: 'Speed Profiles', icon: WifiHigh },
      { path: '/noc-auth-presets', label: 'Auth Presets', icon: GearSix },
      { path: '/noc-general', label: 'General', icon: GearSix },
      { path: '/noc-sync-logs', label: 'Sync Logs', icon: ArrowsClockwise },
    ]},
    { path: '/imft', label: 'IMFT', icon: Wrench },
    { path: '/bmft', label: 'BMFT', icon: Path },
    { path: '/odb-search', label: 'ODB Search', icon: MagnifyingGlass },
    { path: '/fiber-map', label: 'Fiber Map', icon: MapTrifold },
    { path: '/marketing', label: 'Marketing', icon: Megaphone },
    { path: '/store', label: 'Store', icon: Package },
    { path: '/finance', label: 'Finance', icon: CurrencyDollar },
    { path: '/accounting', label: 'Accounting', icon: Scales },
    { path: '/hr', label: 'HR & Staff', icon: UsersThree },
    { path: '/reports', label: 'Reports', icon: FileText },
    { path: '/ai-manager', label: 'AI Manager', icon: Robot },
    { path: '/admin-schedule', label: 'Schedule View', icon: CalendarBlank },
  ],
  Operator: [
    { path: '/customers', label: 'Customers', icon: User },
    { path: '/operator', label: 'Tickets', icon: Headset },
    { path: '/service-ops', label: 'Service Ops', icon: ClipboardText },
    { path: '/odb-search', label: 'ODB Search', icon: MagnifyingGlass },
    { path: '/fiber-map', label: 'Fiber Map', icon: MapTrifold },
  ],
  IMFT: [
    { path: '/imft', label: 'IMFT Tasks', icon: Wrench },
    { path: '/odb-search', label: 'ODB Search', icon: MagnifyingGlass },
    { path: '/fiber-map', label: 'Fiber Map', icon: MapTrifold },
  ],
  'IMFT Supervisor': [
    { path: '/imft', label: 'IMFT Module', icon: Wrench },
    { path: '/store', label: 'Store', icon: Package },
    { path: '/odb-search', label: 'ODB Search', icon: MagnifyingGlass },
    { path: '/fiber-map', label: 'Fiber Map', icon: MapTrifold },
  ],
  BMFT: [
    { path: '/bmft', label: 'BMFT Tasks', icon: Path },
    { path: '/odb-search', label: 'ODB Search', icon: MagnifyingGlass },
    { path: '/fiber-map', label: 'Fiber Map', icon: MapTrifold },
  ],
  'BMFT Supervisor': [
    { path: '/bmft', label: 'BMFT Module', icon: Path },
    { path: '/store', label: 'Store', icon: Package },
    { path: '/odb-search', label: 'ODB Search', icon: MagnifyingGlass },
    { path: '/fiber-map', label: 'Fiber Map', icon: MapTrifold },
  ],
  Marketing: [
    { path: '/marketing', label: 'Sales', icon: Megaphone },
    { path: '/odb-search', label: 'ODB Search', icon: MagnifyingGlass },
    { path: '/fiber-map', label: 'Fiber Map', icon: MapTrifold },
  ],
  'Sales Manager': [
    { path: '/marketing', label: 'Sales Management', icon: Megaphone },
    { path: '/odb-search', label: 'ODB Search', icon: MagnifyingGlass },
    { path: '/fiber-map', label: 'Fiber Map', icon: MapTrifold },
  ],
  'Sales Staff': [
    { path: '/marketing', label: 'My Sales', icon: Megaphone },
    { path: '/odb-search', label: 'ODB Search', icon: MagnifyingGlass },
    { path: '/fiber-map', label: 'Fiber Map', icon: MapTrifold },
  ],
  Finance: [
    { path: '/finance', label: 'Payments', icon: CurrencyDollar },
    { path: '/accounting', label: 'Accounting', icon: Scales },
    { path: '/odb-search', label: 'ODB Search', icon: MagnifyingGlass },
    { path: '/fiber-map', label: 'Fiber Map', icon: MapTrifold },
  ],
  Store: [
    { path: '/store', label: 'Inventory', icon: Package },
    { path: '/odb-search', label: 'ODB Search', icon: MagnifyingGlass },
    { path: '/fiber-map', label: 'Fiber Map', icon: MapTrifold },
  ],
  HR: [
    { path: '/hr', label: 'HR & Staff', icon: UsersThree },
    { path: '/odb-search', label: 'ODB Search', icon: MagnifyingGlass },
    { path: '/fiber-map', label: 'Fiber Map', icon: MapTrifold },
  ],
  NOC: [
    { path: '/noc', label: 'NOC Center', icon: ShieldCheck, subItems: [
      { path: '/noc-olt?configure=1', label: 'MikroTik Configure', icon: GearSix },
      { path: '/noc-olt', label: 'OLT Management', icon: HardDrives },
      { path: '/noc-onu', label: 'ONU List', icon: Cpu },
      { path: '/noc-tr069', label: 'TR-069 / ACS', icon: GearSix },
      { path: '/noc-onu-auth', label: 'ONU Authorization', icon: ShieldCheck },
      { path: '/noc-sync-logs', label: 'Sync Logs', icon: ArrowsClockwise },
    ]},
    { path: '/noc-olt?configure=1', label: 'MikroTik Configure', icon: GearSix },
    { path: '/noc-olt', label: 'OLT Management', icon: WifiHigh },
    { path: '/noc-onu', label: 'ONU Management', icon: Globe },
    { path: '/noc-tr069', label: 'TR-069 ACS', icon: Robot },
    { path: '/noc-onu-auth', label: 'ONU Authorization', icon: ShieldCheck },
    { path: '/noc-sync-logs', label: 'Sync Logs', icon: ClipboardText },
    { path: '/operator', label: 'Tickets', icon: Headset },
    { path: '/bmft', label: 'BMFT', icon: Path },
    { path: '/reports', label: 'Reports', icon: FileText },
    { path: '/odb-search', label: 'ODB Search', icon: MagnifyingGlass },
    { path: '/fiber-map', label: 'Fiber Map', icon: MapTrifold },
  ],
  'General Manager': [
    { path: '/gm-dashboard', label: 'GM Dashboard', icon: Star },
    { path: '/customers', label: 'Customers', icon: User },
    { path: '/marketing', label: 'Marketing', icon: Megaphone },
    { path: '/finance', label: 'Finance', icon: CurrencyDollar },
    { path: '/hr', label: 'HR & Staff', icon: UsersThree },
    { path: '/noc', label: 'NOC Center', icon: ShieldCheck },
    { path: '/store', label: 'Store', icon: Package },
    { path: '/reports', label: 'Reports', icon: FileText },
    { path: '/odb-search', label: 'ODB Search', icon: MagnifyingGlass },
    { path: '/fiber-map', label: 'Fiber Map', icon: MapTrifold },
  ],
};

export const Layout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isMikroTikWorkspace = location.pathname === '/noc-mikrotik';
  const isFullViewportWorkspace = isMikroTikWorkspace || location.pathname === '/fiber-map';
  const [sideOpen, setSideOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState(() => {
    // Auto-expand NOC if on a NOC sub-page
    const path = window.location.pathname;
    if (path.startsWith('/noc')) return { '/noc': true };
    return {};
  });

  const normalizeNocRoute = (entry) => entry.path === '/noc-olt?configure=1' ? { ...entry, path: '/noc-mikrotik' } : entry;
  const rawNavItems = ROLE_NAV[user?.role] || ROLE_NAV[user?.department] || [{ path: '/operator', label: 'Tickets', icon: Headset }];
  const navItems = rawNavItems.map((item) => {
    const normalized = normalizeNocRoute(item);
    return item.subItems ? { ...normalized, subItems: item.subItems.map(normalizeNocRoute) } : normalized;
  });
  const toggleExpand = (path) => setExpandedItems(prev => ({ ...prev, [path]: !prev[path] }));
  const isActive = (p) => location.pathname === p || (p !== '/dashboard' && location.pathname.startsWith(p));
  const notifyMikroTikConfigure = (path) => {
    if (path === '/noc-olt?configure=1') window.setTimeout(() => window.dispatchEvent(new Event('mikrotik-configure-open')), 0);
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  // Global Search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchTimeout = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (val) => {
    setSearchQuery(val);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (val.length < 2) { setSearchResults([]); setSearchOpen(false); setSearchLoading(false); return; }
    searchTimeout.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const token = localStorage.getItem('session_token');
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/search?q=${encodeURIComponent(val)}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (res.ok) { const data = await res.json(); setSearchResults(Array.isArray(data) ? data : []); }
        else setSearchResults([]);
      } catch { setSearchResults([]); }
      finally { setSearchLoading(false); setSearchOpen(true); }
    }, 300);
  };

  const submitSearch = (e) => {
    e?.preventDefault();
    const query = searchQuery.trim();
    searchInputRef.current?.focus();
    if (query.length >= 2) {
      handleSearch(query);
      setSearchOpen(true);
    }
  };

  const goToCustomer = (username) => { setSearchOpen(false); setSearchQuery(''); navigate(`/customers/${username}`); };
  const visibleSearchResults = searchResults.slice(0, 20);
  const groupedSearchResults = SEARCH_GROUP_ORDER.map(type => ({ type, items: visibleSearchResults.filter(result => (result.search_type || 'customer') === type) })).filter(group => group.items.length > 0);
  const searchDisplayRows = groupedSearchResults.flatMap(group => ([{ __group: true, type: group.type, count: group.items.length }, ...group.items]));
  const openSearchResult = (result) => {
    setSearchOpen(false);
    setSearchQuery('');
    const lat = Number(result.lat);
    const lng = Number(result.lng);
    const canFocusMap = Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0);
    if (!canFocusMap) { goToCustomer(result.username); return; }
    const params = new URLSearchParams({ focus: `${lat},${lng}` });
    if (result.search_type === 'customer') params.set('username', result.username);
    else params.set('object', result.object_id);
    navigate(`/fiber-map?${params.toString()}`);
  };

  const openLocationTool = (tool) => {
    const query = searchQuery.trim();
    if (query.length < 2) return;
    setSearchOpen(false);
    setSearchQuery('');
    navigate(`/fiber-map?search_tool=${encodeURIComponent(tool)}&q=${encodeURIComponent(query)}`);
  };

  return (
    <div className="flex h-screen overflow-hidden" data-testid="app-layout">
      {/* Desktop Sidebar */}
      <aside className="desktop-sidebar w-[260px] flex-shrink-0 bg-white border-r border-[var(--md-outline-variant)] flex flex-col">
        <div className="h-16 flex items-center gap-3 px-5 border-b border-[var(--md-outline-variant)]">
          <div className="w-9 h-9 bg-[var(--md-primary)] rounded-xl flex items-center justify-center">
            <WifiHigh size={20} weight="bold" className="text-white" />
          </div>
          <div>
            <span className="font-bold text-[13px] text-[var(--md-on-surface)]">Myanmar Link</span>
            <span className="text-[10px] text-[var(--md-on-surface-variant)] block leading-tight">ISP Management</span>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-1" data-testid="sidebar-nav">
          {navItems.map(item => (
            <div key={item.path}>
              {item.subItems ? (
                <>
                  <button
                    onClick={() => toggleExpand(item.path)}
                    className={`md3-nav-item w-full ${isActive(item.path) ? 'active' : ''}`}
                    data-testid={`nav-${item.path.slice(1)}`}
                  >
                    <item.icon size={20} weight={isActive(item.path) ? 'fill' : 'regular'} />
                    <span className="flex-1 text-left">{item.label}</span>
                    {expandedItems[item.path] ? <CaretDown size={14} /> : <CaretRight size={14} />}
                  </button>
                  {expandedItems[item.path] && (
                    <div className="ml-4 mt-0.5 space-y-0.5 border-l-2 border-[var(--md-outline-variant)] pl-2">
                      <Link to={item.path} className={`md3-nav-item text-[12px] py-1.5 ${location.pathname === item.path ? 'active' : ''}`}>
                        <ShieldCheck size={15} weight={location.pathname === item.path ? 'fill' : 'regular'} />
                        <span>Overview</span>
                      </Link>
                      {item.subItems.map(sub => (
                        <Link key={sub.path} to={sub.path} onClick={() => notifyMikroTikConfigure(sub.path)} className={`md3-nav-item text-[12px] py-1.5 ${isActive(sub.path) ? 'active' : ''}`}>
                          <sub.icon size={15} weight={isActive(sub.path) ? 'fill' : 'regular'} />
                          <span>{sub.label}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <Link key={item.path} to={item.path} onClick={() => notifyMikroTikConfigure(item.path)} data-testid={`nav-${item.path.slice(1)}`}
                  className={`md3-nav-item ${isActive(item.path) ? 'active' : ''}`}>
                  <item.icon size={20} weight={isActive(item.path) ? 'fill' : 'regular'} />
                  <span>{item.label}</span>
                </Link>
              )}
            </div>
          ))}
        </nav>
        <div className="border-t border-[var(--md-outline-variant)] p-3">
          <div className="flex items-center gap-3 p-2 rounded-xl">
            <Avatar className="w-9 h-9"><AvatarFallback className="bg-[var(--md-primary)] text-white text-xs font-medium">{user?.name?.[0]}</AvatarFallback></Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-[10px] text-[var(--md-on-surface-variant)]">{user?.department}</p>
            </div>
            <button onClick={handleLogout} className="p-2 rounded-full hover:bg-[var(--md-surface-2)]" data-testid="logout-btn"><SignOut size={18} className="text-[var(--md-error)]" /></button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay sidebar */}
      {sideOpen && <div className="fixed inset-0 z-40 bg-black/30 md:hidden" onClick={() => setSideOpen(false)} />}
      {sideOpen && (
        <div className="fixed left-0 top-0 bottom-0 w-[280px] bg-white z-50 flex flex-col md:hidden shadow-xl">
          <div className="h-16 flex items-center justify-between px-4 border-b border-[var(--md-outline-variant)]">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[var(--md-primary)] rounded-lg flex items-center justify-center"><WifiHigh size={16} weight="bold" className="text-white" /></div>
              <span className="font-bold text-sm">Myanmar Link</span>
            </div>
            <button onClick={() => setSideOpen(false)}><X size={20} /></button>
          </div>
          <nav className="flex-1 py-3 px-3 space-y-1">
            {navItems.map(item => (
              <div key={item.path}>
                {item.subItems ? (
                  <>
                    <button
                      onClick={() => toggleExpand(item.path)}
                      className={`md3-nav-item w-full ${isActive(item.path) ? 'active' : ''}`}
                    >
                      <item.icon size={20} weight={isActive(item.path) ? 'fill' : 'regular'} />
                      <span className="flex-1 text-left">{item.label}</span>
                      {expandedItems[item.path] ? <CaretDown size={14} /> : <CaretRight size={14} />}
                    </button>
                    {expandedItems[item.path] && (
                      <div className="ml-4 mt-0.5 space-y-0.5 border-l-2 border-[var(--md-outline-variant)] pl-2">
                        <Link to={item.path} onClick={() => setSideOpen(false)} className={`md3-nav-item text-[12px] py-1.5 ${location.pathname === item.path ? 'active' : ''}`}>
                          <ShieldCheck size={15} weight={location.pathname === item.path ? 'fill' : 'regular'} />
                          <span>Overview</span>
                        </Link>
                        {item.subItems.map(sub => (
                          <Link key={sub.path} to={sub.path} onClick={() => setSideOpen(false)} className={`md3-nav-item text-[12px] py-1.5 ${isActive(sub.path) ? 'active' : ''}`}>
                            <sub.icon size={15} weight={isActive(sub.path) ? 'fill' : 'regular'} />
                            <span>{sub.label}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link to={item.path} onClick={() => setSideOpen(false)}
                    className={`md3-nav-item ${isActive(item.path) ? 'active' : ''}`}>
                    <item.icon size={20} weight={isActive(item.path) ? 'fill' : 'regular'} />
                    <span>{item.label}</span>
                  </Link>
                )}
              </div>
            ))}
          </nav>
          <div className="p-3 border-t">
            <button onClick={handleLogout} className="md3-nav-item w-full text-[var(--md-error)]"><SignOut size={20} /> Logout</button>
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        <header className="relative z-[1100] bg-white border-b border-[var(--md-outline-variant)] flex items-center px-4 md:px-6 gap-4 flex-shrink-0" style={{paddingTop: "max(env(safe-area-inset-top), 0px)", minHeight: "4rem"}}>
          <div className="flex items-center gap-3 flex-shrink-0">
            <button onClick={() => setSideOpen(true)} className="md:hidden p-1.5 rounded-full hover:bg-[var(--md-surface-2)]"><List size={22} /></button>
            <h1 className="text-base font-medium hidden md:block">{getTitle(location.pathname)}</h1>
          </div>
          {/* Global Search */}
          <div className="relative flex-1 max-w-xl" ref={searchRef}>
            <form onSubmit={submitSearch} className="flex items-center bg-white border border-[#D0D5DD] rounded-lg overflow-hidden shadow-sm hover:shadow transition-shadow">
              <button type="submit" onClick={submitSearch} title="Search the Fiber Map and customer records" className="px-4 py-2.5 text-[15px] font-semibold text-[#1A1C1E] border-r border-[#D0D5DD] select-none flex-shrink-0 hover:bg-[#F3F7FF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#1565C0]">Search</button>
              <input ref={searchInputRef} value={searchQuery} onChange={e => handleSearch(e.target.value)} onFocus={() => searchResults.length > 0 && setSearchOpen(true)}
                placeholder="Map: ODB/FAT · OLT · Splitter · Cable · Customer/PPPoE · Phone · Location"
                title="Search the map for ODB/FAT, OLT, splitter, fiber cable, customer or PPPoE, phone, and location"
                aria-label="Global map and customer search"
                className="flex-1 px-4 py-2.5 text-[14px] text-[#1A1C1E] placeholder:text-[#8A9099] bg-transparent outline-none border-none" data-testid="global-search" />
            </form>
            {searchOpen && searchQuery.length >= 2 && (
              <div className="absolute top-full left-0 right-0 mt-1 overflow-y-auto rounded-xl border border-[var(--md-outline-variant)] bg-white shadow-lg z-[1110] max-h-[280px]" data-testid="search-results">
                <div className="sticky top-0 z-20 border-b border-[var(--md-outline-variant)] bg-white px-3 py-1.5 text-[10px] text-[var(--md-on-surface-variant)]">{searchLoading ? 'Searching…' : `${searchResults.length} match(es) · showing top ${visibleSearchResults.length}`}</div>
                <div className="grid grid-cols-2 gap-2 border-b border-[var(--md-outline-variant)] bg-slate-50 p-2">
                  <button type="button" onClick={() => openLocationTool('coverage')} className="rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-2 text-left transition-colors hover:bg-blue-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600">
                    <span className="block text-[11px] font-semibold text-blue-800">📍 Coverage Check</span>
                    <span className="mt-0.5 block truncate text-[9px] text-blue-700">Check mapped ODB coverage for “{searchQuery.trim()}”</span>
                  </button>
                  <button type="button" onClick={() => openLocationTool('customer')} className="rounded-lg border border-violet-200 bg-violet-50 px-2.5 py-2 text-left transition-colors hover:bg-violet-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600">
                    <span className="block text-[11px] font-semibold text-violet-800">👤 Customer Lookup</span>
                    <span className="mt-0.5 block truncate text-[9px] text-violet-700">Find existing customers for “{searchQuery.trim()}”</span>
                  </button>
                </div>
                {!searchLoading && searchDisplayRows.map((r, i) => {
                  if (r.__group) {
                    const meta = SEARCH_TYPE_META[r.type];
                    return <div key={`group-${r.type}`} className="sticky top-0 z-10 border-y border-slate-100 bg-slate-50 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">{meta.label} ({r.count})</div>;
                  }
                  // ── Compact FTTX map rows ──
                  if (r.search_type === 'odb') {
                    const portText = Number.isFinite(Number(r.capacity)) ? `${r.user_count || 0}/${r.capacity} ports` : '';
                    return (
                      <button key={`${r.search_type}-${r.object_id}-${i}`} className="w-full border-b border-[var(--md-outline-variant)] bg-[#FFFDF7] px-3 py-2 text-left transition-colors last:border-0 hover:bg-[#FFF8E1]" onClick={() => openSearchResult(r)}>
                        <div className="flex items-center gap-2.5">
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[#FFF3E0] text-[13px]">📦</div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <p className="truncate text-[12px] font-semibold text-[#9A3412]">{r.odb_name || r.name || r.object_id}</p>
                              <span className="rounded border border-orange-200 bg-orange-50 px-1 py-0.5 text-[8px] font-bold text-orange-700">ODB</span>
                              {portText && <span className="text-[9px] text-slate-500">{portText}</span>}
                            </div>
                            <p className="truncate text-[9px] text-slate-500">{[r.object_id, r.description || r.source_file].filter(Boolean).join(' · ')}</p>
                          </div>
                          <span className="text-sm text-slate-400">›</span>
                        </div>
                      </button>
                    );
                  }
                  if (['olt', 'splitter', 'backbone_route', 'distribution_route', 'drop_cable'].includes(r.search_type)) {
                    const meta = SEARCH_TYPE_META[r.search_type];
                    const isOlt = r.search_type === 'olt';
                    return (
                      <button key={`${r.search_type}-${r.object_id}-${i}`} className={`w-full border-b border-[var(--md-outline-variant)] px-3 py-2 text-left transition-colors last:border-0 ${isOlt ? 'bg-[#FFF8F8] hover:bg-[#FFEBEE]' : r.search_type === 'splitter' ? 'bg-[#FFFDF5] hover:bg-[#FFF9C4]' : 'bg-slate-50 hover:bg-slate-100'}`} onClick={() => openSearchResult(r)}>
                        <div className="flex items-center gap-2.5">
                          <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[13px] ${isOlt ? 'bg-[#FFCDD2]' : r.search_type === 'splitter' ? 'bg-[#FFF9C4]' : 'bg-slate-200'}`}>{meta.icon}</div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <p className={`truncate text-[12px] font-semibold ${isOlt ? 'text-[#B71C1C]' : r.search_type === 'splitter' ? 'text-[#A16207]' : 'text-slate-700'}`}>{r.name || r.object_id}</p>
                              <span className={`rounded border px-1 py-0.5 text-[8px] font-bold ${meta.tone}`}>{meta.badge}</span>
                            </div>
                            <p className="truncate text-[9px] text-slate-500">{[r.object_id, r.description || r.source_file].filter(Boolean).join(' · ')}</p>
                          </div>
                          <span className="text-sm text-slate-400">›</span>
                        </div>
                      </button>
                    );
                  }
                  // ── Compact customer row ──
                  const statusColor = r.account_status === 'active' ? 'bg-[#E8F5E9] text-[#1B5E20]' : r.account_status === 'bill_required' ? 'bg-[#FFF8E1] text-[#E65100]' : 'bg-[#E3F2FD] text-[#0D47A1]';
                  const avatarColor = r.account_status === 'active' ? 'bg-[#E8F5E9]' : r.account_status === 'bill_required' ? 'bg-[#FFF8E1]' : 'bg-[#E3F2FD]';
                  const avatarIcon = r.account_status === 'active' ? 'text-[#2E7D32]' : r.account_status === 'bill_required' ? 'text-[#E65100]' : 'text-[#1565C0]';
                  const hasCustomerMapLocation = Number.isFinite(Number(r.lat)) && Number.isFinite(Number(r.lng)) && Number(r.lat) !== 0 && Number(r.lng) !== 0;
                  // Display the API's existing, read-only cross-references without conflating a service case and an infrastructure fault.
                  const hasLinkedBoxDown = r.odb_health?.status === 'down';
                  const hasBmftInfraIssue = Boolean(r.bmft_ticket?.task_id);
                  const hasDirectServiceIssue = Boolean(r.pending_ticket?.ticket_id);
                  const directIssueLabel = r.pending_ticket?.type === 'install'
                    ? `Install: ${r.pending_ticket?.status || 'Open'}`
                    : `Service: ${r.pending_ticket?.issue_type || r.pending_ticket?.status || 'Open'}`;
                  return (
                  <div key={`customer-${r.username}-${i}`} className="border-b border-[var(--md-outline-variant)] px-3 py-2 last:border-0">
                    <div className="flex items-center gap-2.5">
                      <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${avatarColor}`}>
                        <User size={13} className={avatarIcon} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="truncate text-[12px] font-semibold">{`${r.firstname || ''} ${r.lastname || ''}`.trim() || r.username}</p>
                          <span className={`shrink-0 rounded px-1 py-0.5 text-[8px] font-bold ${statusColor}`}>{r.map_available ? 'MAP' : 'PROFILE'}</span>
                        </div>
                        <p className="truncate text-[9px] text-slate-500">{[r.username, r.phone || r.mobile, r.address || r.service_name].filter(Boolean).join(' · ')}</p>
                        <div className="mt-1 flex flex-wrap gap-1" aria-label="Current service and infrastructure status">
                          {hasLinkedBoxDown && <span className="max-w-full truncate rounded border border-red-200 bg-red-50 px-1.5 py-0.5 text-[8px] font-bold text-red-700" title={r.odb_health?.issue_id || r.odb_health?.error_type || 'Linked infrastructure fault'}>BOX / LINK DOWN · {r.odb_health?.odb_name || r.odb_health?.error_type || 'Linked network'}</span>}
                          {!hasLinkedBoxDown && hasBmftInfraIssue && <span className="max-w-full truncate rounded border border-orange-200 bg-orange-50 px-1.5 py-0.5 text-[8px] font-bold text-orange-800" title={r.bmft_ticket?.task_id}>INFRA REPAIR · {r.bmft_ticket?.issue_type || r.bmft_ticket?.task_type || 'Active task'} · {r.bmft_ticket?.task_id}</span>}
                          {hasDirectServiceIssue && <span className="max-w-full truncate rounded border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[8px] font-bold text-blue-800" title={r.pending_ticket?.ticket_id}>SERVICE ISSUE · {directIssueLabel} · {r.pending_ticket?.ticket_id}</span>}
                          {!hasLinkedBoxDown && !hasBmftInfraIssue && !hasDirectServiceIssue && <span className="rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[8px] font-semibold text-emerald-800">NO ACTIVE SERVICE / BOX-DOWN RECORD</span>}
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-1.5 pl-9">
                      <button type="button" onClick={() => goToCustomer(r.username)} className="rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-[9px] font-semibold text-blue-800 transition-colors hover:bg-blue-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600">
                        Customer Info
                      </button>
                      {hasCustomerMapLocation ? (
                        <button type="button" onClick={() => openSearchResult(r)} className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-[9px] font-semibold text-emerald-800 transition-colors hover:bg-emerald-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600">
                          Location on Map
                        </button>
                      ) : (
                        <span className="text-[9px] text-amber-700">No saved map location</span>
                      )}
                    </div>
                  </div>
                  );
                })}
                {!searchLoading && searchDisplayRows.length === 0 && <p className="px-3 py-4 text-center text-xs text-slate-500">No results found</p>}
              </div>
            )}
          </div>
          <span className="md3-chip bg-[var(--md-primary-container)] text-[var(--md-on-primary-container)] hidden sm:inline-flex flex-shrink-0">{user?.department}</span>
        </header>
        <div className={`flex-1 overflow-y-auto bg-[var(--md-surface)] main-content ${isFullViewportWorkspace ? 'overflow-hidden' : ''}`}>
          <div className={isFullViewportWorkspace ? 'w-full h-full min-h-full page-enter' : 'max-w-[1400px] mx-auto p-5 md:p-6 page-enter'}><Outlet /></div>
        </div>
      </main>

      {/* Mobile bottom nav */}
      <div className="mobile-bottom-nav fixed bottom-0 left-0 right-0 bg-white border-t border-[var(--md-outline-variant)] items-center justify-around z-30 px-2" style={{display:"none", paddingBottom: "max(env(safe-area-inset-bottom), 0px)", minHeight: "4rem"}}>
        {navItems.slice(0, 5).map(item => (
          <Link key={item.path} to={item.path} className="flex flex-col items-center gap-0.5 py-1 px-3">
            <div className={`p-1 rounded-full ${isActive(item.path) ? 'bg-[var(--md-primary-container)]' : ''}`}>
              <item.icon size={20} weight={isActive(item.path) ? 'fill' : 'regular'} className={isActive(item.path) ? 'text-[var(--md-primary)]' : 'text-[var(--md-on-surface-variant)]'} />
            </div>
            <span className={`text-[10px] ${isActive(item.path) ? 'text-[var(--md-primary)] font-medium' : 'text-[var(--md-on-surface-variant)]'}`}>{item.label}</span>
          </Link>
        ))}
      </div>
    </div>
  );
};

function getTitle(p) {
  const t = { '/dashboard': 'Dashboard', '/customers': 'Customers', '/operator': 'Operator - Tickets', '/service-ops': 'Service Operations', '/noc': 'NOC Operations Center', '/imft': 'IMFT - Install & Maintain', '/bmft': 'BMFT - Backbone Maintenance', '/fiber-map': 'Fiber Network Map', '/marketing': 'Marketing - Sales', '/store': 'Store & Inventory', '/finance': 'Finance - Payments', '/hr': 'HR & Staff', '/reports': 'Reports', '/ai-manager': 'AI Manager', '/admin-schedule': 'Daily Schedule View', '/onu-list': 'ONU List', '/noc-mikrotik': 'MikroTik Configure', '/noc-olt': 'OLT Management', '/noc-onu': 'ONU Management (NOC)', '/noc-tr069': 'TR-069 ACS', '/noc-sync-logs': 'Sync Logs' };
  return Object.entries(t).sort(([a],[b]) => b.length - a.length).find(([k]) => p.startsWith(k))?.[1] || 'Myanmar Link ISP';
}
